// ./src/utils/api/VolunteerAssignmentService.ts
//
// VolunteerAssignment (a crew member assigned to a volunteer slot) service.
// OpResult-native, event-scoped. Mirrors PoiSignupService. A bulk update/remove is
// a multi-id op (server restricts a bulk update to status).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";
import type { ServicePointRef } from "./ServicePointService";

// Attendance transitions (mirror the eventsmagic-api VOLUNTEER_ATTENDANCE_TRANSITION boundary).
export const VOLUNTEER_ATTENDANCE = {
  CHECK_IN: "check_in",
  CHECK_OUT: "check_out",
  UNDO_CHECK_IN: "undo_check_in",
  UNDO_CHECK_OUT: "undo_check_out",
  NO_SHOW: "no_show",
  CLEAR_NO_SHOW: "clear_no_show",
} as const;

export type VolunteerAttendanceAction = (typeof VOLUNTEER_ATTENDANCE)[keyof typeof VOLUNTEER_ATTENDANCE];

/**
 * What a QR SCAN does — the check-in console's mode (mirrors the API's VOLUNTEER_SCAN_ACTION).
 *
 * ⚠ A DELIBERATE SUBSET of VOLUNTEER_ATTENDANCE above. Undo and no-show are per-row corrections and
 * must never be something a stray scan can cause: a badge left face-up on the desk should not be
 * able to mark somebody a no-show. The server refuses anything outside this pair.
 */
export const VOLUNTEER_SCAN_ACTION = {
  CHECK_IN: "check_in",
  CHECK_OUT: "check_out",
} as const;

export type VolunteerScanAction = (typeof VOLUNTEER_SCAN_ACTION)[keyof typeof VOLUNTEER_SCAN_ACTION];

const VOLUNTEER_ASSIGNMENT_FIELDS = `
  id
  eventId
  eventVolunteerSlotId
  eventGuestId
  note
  checkedInAt
  checkedOutAt
  checkedInByUserId
  checkedOutByUserId
  checkedInServicePoint { id name kind removedAt }
  checkedOutServicePoint { id name kind removedAt }
  noShow
  payRate
  breakMinutes
  bonusAmount
  bonusNote
  budgetPaymentId
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query VolunteerAssignmentList($filter: EventVolunteerAssignmentFilter, $params: PaginationAndSorting) {
    eventVolunteerAssignmentList(filter: $filter, params: $params) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query VolunteerAssignmentGet($id: ID) {
    eventVolunteerAssignmentGet(id: $id) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation VolunteerAssignmentCreate($params: EventVolunteerAssignmentInput) {
    eventVolunteerAssignmentCreate(params: $params) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation VolunteerAssignmentUpdate($where: EventVolunteerAssignmentWhereInput, $params: EventVolunteerAssignmentInput) {
    eventVolunteerAssignmentUpdate(where: $where, params: $params) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation VolunteerAssignmentRemove($where: EventVolunteerAssignmentWhereInput) {
    eventVolunteerAssignmentRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const ATTENDANCE_MUTATION = gql`
  mutation VolunteerAttendance($where: EventVolunteerAssignmentWhereInput, $params: EventVolunteerAttendanceInput) {
    eventVolunteerAttendance(where: $where, params: $params) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CHECK_IN_BY_CODE_MUTATION = gql`
  mutation VolunteerCheckInByCode($params: EventVolunteerCheckInByCodeInput) {
    eventVolunteerCheckInByCode(params: $params) {
      code
      data { ${VOLUNTEER_ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export interface VolunteerAssignment {
  id?: string;
  eventId?: string | null;
  eventVolunteerSlotId?: string | null;
  eventGuestId?: string | null;
  note?: string | null;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  checkedInByUserId?: string | null;
  checkedOutByUserId?: string | null;
  /** #169 — where the check-in / check-out was scanned (null = not recorded). */
  checkedInServicePoint?: ServicePointRef | null;
  checkedOutServicePoint?: ServicePointRef | null;
  noShow?: boolean | null;
  /** #A4.7 — the hourly rate SNAPSHOT taken at check-in (null before, or for an unpaid shift). */
  payRate?: number | null;
  /** #A4.7 — an unpaid break taken during the shift, entered by crew (never automatic). */
  breakMinutes?: number | null;
  /** #A4.7 — a bonus on top of the shift's pay (event currency); shown to the volunteer with its note. */
  bonusAmount?: number | null;
  bonusNote?: string | null;
  /** #A4.7 — the budget payment this shift was paid by ("Mark paid"); null = not paid yet. */
  budgetPaymentId?: string | null;
  orderNo?: number;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface VolunteerAssignmentInput {
  eventId?: string | null;
  eventVolunteerSlotId?: string | null;
  eventGuestId?: string | null;
  note?: string | null;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  noShow?: boolean | null;
  breakMinutes?: number | null;
  /** ⚠ Send the bonus ONLY when the caller may manage the budget — crew without budget.edit are refused for it. */
  bonusAmount?: number | null;
  bonusNote?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface VolunteerAssignmentFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  eventVolunteerSlotId?: string[] | null;
  eventGuestId?: string[] | null;
  status?: number[] | null;
}

export interface VolunteerAssignmentWhereInput {
  id?: string | string[];
}

export class VolunteerAssignmentService {
  static async list(filter?: VolunteerAssignmentFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more button,
   * so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: VolunteerAssignmentFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<VolunteerAssignment>(VolunteerAssignmentService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: VolunteerAssignmentInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: VolunteerAssignmentWhereInput, params: VolunteerAssignmentInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id: ids } });
  }

  /** Server-stamped attendance change (check in / out / undo / no-show) on one or many assignments. */
  static async attendance(
    ids: string | string[],
    action: VolunteerAttendanceAction,
    // #169 — the point the scanner stands at (the volunteer desk), recorded with the stamp.
    opts: { servicePointId?: string | null } = {},
  ): Promise<OpResult> {
    const list = Array.isArray(ids) ? ids : [ids];
    if (list.length === 0) {
      return new OpResult();
    }
    return ApiService.executeMutation(ATTENDANCE_MUTATION, {
      where: { id: list },
      params: { action, ...(opts.servicePointId ? { servicePointId: opts.servicePointId } : {}) },
    });
  }

  /**
   * Check a volunteer IN or OUT by the guest code on their QR (#A4.6; check-out added 2026-09-22).
   *
   * The SERVER picks the shift — for check-in the one under way (or starting within the hour / next);
   * for check-out the one open, or the one ending soonest when several are. An ambiguous or unknown
   * code is refused with a message keyed `guestCode`, and the two modes refuse in different words.
   *
   * ⚠ `action` is REQUIRED here even though the SDL defaults it. The default exists so an OLD client
   * keeps working; a caller in THIS codebase has a mode on screen and must say which it is, or the
   * console would silently check people in while its operator watched a button saying "Check out".
   */
  static async scanByCode(
    eventId: string,
    guestCode: string,
    action: VolunteerScanAction,
    opts: { servicePointId?: string | null } = {},
  ): Promise<OpResult> {
    return ApiService.executeMutation(CHECK_IN_BY_CODE_MUTATION, {
      params: { eventId, guestCode, action, ...(opts.servicePointId ? { servicePointId: opts.servicePointId } : {}) },
    });
  }
}
